import { useMemo, type ReactNode } from "react";
import { useActiveAccount } from "applesauce-react/hooks";
import { useSeoMeta } from "@unhead/react";
import { formatDistanceToNow } from "date-fns";
import { nip19 } from "nostr-tools";
import { Link, useParams } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Cpu,
  GitBranch,
  Globe2,
  KeyRound,
  LockKeyhole,
  RadioTower,
  ShieldCheck,
  ShieldQuestion,
  Sparkles,
  Users,
  Workflow,
  XCircle,
} from "lucide-react";
import type { CICoordinatorAdvertisement } from "@/casts/CICoordinator";
import { CIStatusIcon } from "@/components/ci/CIStatusIcon";
import { GraspLogo } from "@/components/GraspLogo";
import { RepoBadge } from "@/components/RepoBadge";
import { UserAvatar, UserLink } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthModal } from "@/contexts/AuthModalContext";
import {
  useCICoordinatorProfile,
  type CICoordinatorProfileState,
} from "@/hooks/useCICoordinatorProfile";
import { useCoordinatorRecentRuns } from "@/hooks/useCoordinatorRecentRuns";
import { useCoordinatorServiceIdentity } from "@/hooks/useCoordinatorServiceIdentity";
import {
  useFollowRepositoryPresence,
  useGraspServiceCounts,
  usePrivateGraspAccessCheck,
  type FollowRepositoryPresence,
  type PersonalGraspCounts,
  type PrivateGraspAccessState,
  type CountValue,
} from "@/hooks/useGraspServiceInsights";
import { useGraspServerInfo } from "@/hooks/useGraspServerInfo";
import { useDefaultRepoCoordPath } from "@/hooks/useRepoPath";
import { useUserRepositories } from "@/hooks/useUserRepositories";
import {
  ciStatusLabel,
  workflowRunRepoCoords,
  type CIWorkflowRun,
} from "@/lib/ci";
import {
  getGraspAccessSummary,
  type GraspAccessSummary,
  type Nip11Document,
} from "@/lib/grasp";
import { parseRepoCoordinate, type ResolvedRepo } from "@/lib/nip34";
import { decodePubkeyIdentifier, parseRelayUrl } from "@/lib/routeUtils";
import { cn } from "@/lib/utils";
import NotFound from "@/pages/NotFound";
import RepositoriesPage from "@/pages/RepositoriesPage";

function hostFromRelay(relayUrl: string | undefined): string | undefined {
  if (!relayUrl) return undefined;
  try {
    return new URL(relayUrl).host;
  } catch {
    return undefined;
  }
}

function coordinatorRepoCoordinate(
  repo: ResolvedRepo,
  targetPubkeys: ReadonlySet<string>,
): string {
  return (
    repo.allCoordinates.find((coordinate) => {
      const parsed = parseRepoCoordinate(coordinate);
      return parsed ? targetPubkeys.has(parsed.pubkey) : false;
    }) ?? repo.selectedCoordinate
  );
}

function formatCount(value: number | undefined): string {
  return value?.toLocaleString() ?? "";
}

function uniqueCoordinates(coordinates: string[]): string[] {
  return [...new Set(coordinates)];
}

export default function CICoordinatorPage() {
  const { coordinatorIdentifier = "", relaySegment = "" } = useParams();
  const relayUrl = useMemo(
    () => (relaySegment ? parseRelayUrl(relaySegment) : undefined),
    [relaySegment],
  );
  const relayDomain = hostFromRelay(relayUrl);
  const routeServer = useGraspServerInfo(relayDomain);
  const serverDocument =
    routeServer?.status === "found" ? routeServer.document : undefined;
  const serverPubkey = serverDocument?.pubkey
    ? decodePubkeyIdentifier(serverDocument.pubkey)
    : undefined;
  const routePubkey = coordinatorIdentifier
    ? decodePubkeyIdentifier(coordinatorIdentifier)
    : undefined;
  const pubkey = routePubkey ?? serverPubkey;
  const identity = useCoordinatorServiceIdentity(pubkey);
  const state = useCICoordinatorProfile(pubkey);
  const account = useActiveAccount();
  const myRepositories = useUserRepositories(account?.pubkey);
  const serviceState = relayDomain ? routeServer : identity?.server;
  const document =
    serviceState?.status === "found" ? serviceState.document : undefined;
  const isGraspService = (document?.supported_grasps?.length ?? 0) > 0;
  const domain = relayDomain ?? identity?.domain;
  const serviceRelayUrl =
    relayUrl ?? (isGraspService && domain ? `wss://${domain}` : undefined);
  const access = document ? getGraspAccessSummary(document) : undefined;
  const counts = useGraspServiceCounts(
    isGraspService ? serviceRelayUrl : undefined,
  );
  const follows = useFollowRepositoryPresence(
    isGraspService && access?.mode !== "private" ? serviceRelayUrl : undefined,
  );
  const accessCheck = usePrivateGraspAccessCheck(
    access?.mode === "private" ? serviceRelayUrl : undefined,
  );
  const recentRuns = useCoordinatorRecentRuns(pubkey, state, serviceRelayUrl);
  const { openAuthModal } = useAuthModal();

  const now = Math.floor(Date.now() / 1000);
  const advertisementIsLive =
    state?.advertisement !== undefined && state.advertisement.expiration > now;
  const readinessIsLive =
    advertisementIsLive &&
    state?.readiness !== undefined &&
    state.readiness.expiration > now;
  const targetCoordinates = useMemo(() => {
    if (!readinessIsLive || !state?.readiness) return [];
    const activeCoordinates = new Set(
      state.activeStatuses.flatMap((status) => status.repositoryCoordinates),
    );
    const activeRepositories = new Set(
      (state.targetedRepositories ?? [])
        .filter((repo) =>
          repo.allCoordinates.some((coordinate) =>
            activeCoordinates.has(coordinate),
          ),
        )
        .map((repo) => repo.selectedCoordinate),
    );
    const targetPubkeys = new Set(state.readiness.repositoryPubkeys);
    const coordinates = [
      ...state.readiness.repositoryCoordinates,
      ...(state.targetedRepositories ?? []).map((repo) =>
        coordinatorRepoCoordinate(repo, targetPubkeys),
      ),
    ];
    return uniqueCoordinates(
      uniqueCoordinates(coordinates).flatMap((coordinate) => {
        if (activeCoordinates.has(coordinate)) return [];
        const repository = state.targetedRepositories?.find((repo) =>
          repo.allCoordinates.includes(coordinate),
        );
        if (
          repository &&
          activeRepositories.has(repository.selectedCoordinate)
        ) {
          return [];
        }
        return [repository?.selectedCoordinate ?? coordinate];
      }),
    );
  }, [readinessIsLive, state]);
  const activeCoordinates = uniqueCoordinates(
    (state?.activeStatuses ?? []).map((status) => status.selectedCoordinate),
  );
  const activeCoordinateSet = new Set(
    (state?.activeStatuses ?? []).flatMap(
      (status) => status.repositoryCoordinates,
    ),
  );
  const myActiveRepositories = (myRepositories ?? []).filter((repository) =>
    repository.allCoordinates.some((coordinate) =>
      activeCoordinateSet.has(coordinate),
    ),
  );
  const myActiveCoordinateSet = new Set(
    myActiveRepositories.map((repository) => repository.selectedCoordinate),
  );
  const myReadyRepositories =
    readinessIsLive && state?.readiness
      ? (myRepositories ?? []).filter(
          (repository) =>
            !myActiveCoordinateSet.has(repository.selectedCoordinate) &&
            state.readiness?.supportsRepository(
              repository.allCoordinates,
              repository.maintainerSet,
            ),
        )
      : [];
  const isCoordinatorRoute = !!coordinatorIdentifier;
  const hasCoordinatorAdvertisement = state?.advertisement !== undefined;
  const showCoordinator =
    isCoordinatorRoute ||
    hasCoordinatorAdvertisement ||
    activeCoordinates.length > 0;
  const linkedService =
    isGraspService &&
    !!pubkey &&
    identity?.linkedGraspService === true &&
    identity.domain === domain;
  const npub = pubkey ? nip19.npubEncode(pubkey) : undefined;
  const pageTitle =
    domain ?? identity?.label ?? (npub ? `${npub.slice(0, 16)}…` : "Service");
  const pageKind = isGraspService
    ? showCoordinator
      ? "GRASP service and CI coordinator"
      : "GRASP service"
    : showCoordinator
      ? "CI coordinator"
      : "Relay";

  useSeoMeta({
    title: `${pageTitle} - ngit`,
    description: `${pageKind} access, personal activity, repository search, and recent work`,
    ogImage: "/og-image.png",
    twitterCard: "summary_large_image",
  });

  if (coordinatorIdentifier && !routePubkey) return <NotFound />;
  if (relaySegment && !relayUrl) return <NotFound />;
  if (!coordinatorIdentifier && !relaySegment) return <NotFound />;

  return (
    <div className="min-h-full bg-gradient-to-b from-muted/25 via-background to-background">
      <header className="relative isolate overflow-hidden border-b border-border/50 bg-background">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-pink-500/[0.08] via-background to-violet-500/[0.08]" />
        <div className="absolute -right-24 -top-32 -z-10 h-80 w-80 rounded-full bg-pink-500/10 blur-3xl" />
        <div className="container max-w-screen-xl px-4 py-8 md:px-8 md:py-10">
          <Link
            to="/"
            className="mb-7 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
          >
            <ArrowLeft className="h-4 w-4" />
            All repositories
          </Link>

          <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex min-w-0 items-start gap-4 sm:gap-5">
              <ServiceMark
                pubkey={pubkey}
                isGraspService={isGraspService}
                isCoordinator={showCoordinator}
              />
              <div className="min-w-0">
                <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-pink-600 dark:text-pink-400">
                  {pageKind}
                </p>
                <h1 className="break-all text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                  {pageTitle}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {access && <AccessBadge access={access} />}
                  {showCoordinator && (
                    <Badge
                      variant="outline"
                      className={cn(
                        "gap-1.5",
                        advertisementIsLive
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
                      )}
                    >
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          advertisementIsLive
                            ? "bg-emerald-500"
                            : "bg-amber-500",
                        )}
                      />
                      {advertisementIsLive
                        ? "Coordinator available"
                        : "No live coordinator advert"}
                    </Badge>
                  )}
                  {linkedService && (
                    <Badge
                      variant="outline"
                      className="gap-1.5 border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Verified operator
                    </Badge>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                  {identity?.verifiedNip05 && (
                    <span className="font-medium text-foreground">
                      {identity.verifiedNip05}
                    </span>
                  )}
                  {npub && (
                    <span className="font-mono" title={npub}>
                      {npub.slice(0, 20)}…
                    </span>
                  )}
                </div>
              </div>
            </div>

            {isGraspService && (
              <ServiceVolume
                repositories={counts.service?.repositories}
                issues={counts.service?.issues}
                pullRequests={counts.service?.pullRequests}
              />
            )}
          </div>
        </div>
      </header>

      <main className="container max-w-screen-xl space-y-8 px-4 py-8 md:px-8 md:py-10">
        {(relaySegment || isGraspService) && (
          <AccessSection
            access={access}
            document={document}
            serverStatus={serviceState?.status}
            personal={counts.personal}
            signedIn={counts.signedIn}
            checkState={accessCheck.state}
            onCheckAccess={() => void accessCheck.check()}
            onSignIn={() => openAuthModal("sign-in")}
          />
        )}

        {(isGraspService || showCoordinator) && (
          <PersonalSection
            signedIn={!!account}
            personal={isGraspService ? counts.personal : undefined}
            follows={isGraspService ? follows : undefined}
            showServiceActivity={isGraspService}
            serviceActivityUnavailable={access?.mode === "private"}
            showCoordinator={showCoordinator}
            myActiveRepositories={myActiveRepositories}
            myReadyRepositories={myReadyRepositories}
            repositoriesLoading={
              !!account && showCoordinator && myRepositories === undefined
            }
            onSignIn={() => openAuthModal("sign-in")}
          />
        )}

        {showCoordinator && pubkey && (
          <CoordinatorSection
            state={state}
            advertisementIsLive={advertisementIsLive}
            activeCoordinates={activeCoordinates}
            targetCoordinates={targetCoordinates}
            recentRuns={recentRuns}
          />
        )}

        {serviceRelayUrl && (relaySegment || isGraspService) && (
          <section aria-labelledby="service-search-heading">
            <Card className="overflow-hidden border-border/70 shadow-sm">
              <CardHeader className="border-b border-border/50 bg-muted/25">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-pink-500/10 p-2.5 text-pink-600 dark:text-pink-400">
                    <GitBranch className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle id="service-search-heading" className="text-xl">
                      Search this service
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Results come only from {domain ?? serviceRelayUrl}.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <RepositoriesPage
                  key={access?.mode === "private" ? "private" : "public"}
                  embedded
                  relayOverride={[serviceRelayUrl]}
                  relayLabel={domain ?? serviceRelayUrl}
                  relayStatusBanner={
                    access?.mode === "private" ? (
                      <div className="rounded-lg border border-violet-500/20 bg-violet-500/[0.05] px-4 py-3 text-sm text-muted-foreground">
                        Private repository results require an authenticated
                        relay session. The access check above verifies
                        membership without retaining that authentication or
                        private events.
                      </div>
                    ) : undefined
                  }
                />
              </CardContent>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
}

function ServiceMark({
  pubkey,
  isGraspService,
  isCoordinator,
}: {
  pubkey: string | undefined;
  isGraspService: boolean;
  isCoordinator: boolean;
}) {
  if (!isGraspService && pubkey) {
    return (
      <div className="relative shrink-0">
        <UserAvatar
          pubkey={pubkey}
          size="xl"
          className="ring-4 ring-background shadow-lg"
          noHoverCard
        />
        {isCoordinator && (
          <span className="absolute -bottom-1 -right-1 rounded-full border-4 border-background bg-violet-500 p-1 text-white">
            <Cpu className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="relative grid h-16 w-16 shrink-0 place-items-center rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-500/15 to-violet-500/10 text-pink-600 shadow-sm sm:h-20 sm:w-20 dark:text-pink-400">
      <GraspLogo className="h-9 w-9 sm:h-11 sm:w-11" />
      {isCoordinator && (
        <span className="absolute -bottom-1 -right-1 rounded-full border-4 border-background bg-violet-500 p-1 text-white">
          <Cpu className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}

function AccessBadge({ access }: { access: GraspAccessSummary }) {
  const styles = {
    public:
      "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    private:
      "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
    curated:
      "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    unknown: "border-border bg-muted text-muted-foreground",
  } as const;
  return (
    <Badge variant="outline" className={cn("gap-1.5", styles[access.mode])}>
      {access.mode === "public" ? (
        <Globe2 className="h-3.5 w-3.5" />
      ) : access.mode === "private" ? (
        <LockKeyhole className="h-3.5 w-3.5" />
      ) : (
        <ShieldQuestion className="h-3.5 w-3.5" />
      )}
      {access.title}
    </Badge>
  );
}

function ServiceVolume({
  repositories,
  issues,
  pullRequests,
}: {
  repositories: CountValue;
  issues: CountValue;
  pullRequests: CountValue;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      <VolumeMetric label="Repositories" value={repositories} />
      <VolumeMetric label="Issues" value={issues} />
      <VolumeMetric label="Pull requests" value={pullRequests} />
    </div>
  );
}

function VolumeMetric({ label, value }: { label: string; value: CountValue }) {
  return (
    <div className="min-w-0 rounded-xl border border-border/70 bg-background/80 px-3 py-3 text-center shadow-sm backdrop-blur sm:min-w-28 sm:px-4">
      {value === undefined ? (
        <Skeleton className="mx-auto mb-1 h-7 w-12" />
      ) : value === null ? (
        <p
          className="text-xl font-bold text-muted-foreground sm:text-2xl"
          title="Count unavailable"
        >
          —
        </p>
      ) : (
        <p className="text-xl font-bold tabular-nums sm:text-2xl">
          {formatCount(value)}
        </p>
      )}
      <p className="truncate text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function AccessSection({
  access,
  document,
  serverStatus,
  personal,
  signedIn,
  checkState,
  onCheckAccess,
  onSignIn,
}: {
  access: GraspAccessSummary | undefined;
  document: Nip11Document | undefined;
  serverStatus: "loading" | "found" | "error" | undefined;
  personal: PersonalGraspCounts | undefined;
  signedIn: boolean;
  checkState: PrivateGraspAccessState;
  onCheckAccess: () => void;
  onSignIn: () => void;
}) {
  if (serverStatus === "loading" || serverStatus === undefined) {
    return <AccessSkeleton />;
  }

  if (!access || !document || serverStatus === "error") {
    return (
      <Card className="border-amber-500/25 bg-amber-500/[0.04]">
        <CardContent className="flex gap-4 p-6">
          <CircleAlert className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
          <div>
            <h2 className="text-xl font-semibold">
              Access could not be verified
            </h2>
            <p className="mt-1 text-muted-foreground">
              This relay did not provide usable GRASP service information. You
              can still search its public repository announcements below.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const alreadyUsingService = personal
    ? Object.values(personal).some(
        (value) => typeof value === "number" && value > 0,
      )
    : false;

  if (access.mode === "public") {
    if (alreadyUsingService) return null;

    return (
      <section aria-labelledby="access-heading">
        <Card className="border-emerald-500/20 bg-emerald-500/[0.025] shadow-none">
          <CardContent className="flex items-start gap-3 px-4 py-3 sm:items-center">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 sm:mt-0 dark:text-emerald-400" />
            <div className="min-w-0 sm:flex sm:items-baseline sm:gap-2">
              <h2
                id="access-heading"
                className="text-sm font-semibold text-foreground"
              >
                Public service
              </h2>
              <p className="text-sm text-muted-foreground">
                Open to use under normal repository authorization.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  const alreadyPresent = (personal?.repositories ?? 0) > 0;
  const heading =
    access.mode === "private"
      ? checkState === "allowed"
        ? "Yes — you are on the whitelist"
        : checkState === "denied"
          ? "No — this account is not on the whitelist"
          : "Private — check your whitelist access"
      : access.mode === "curated"
        ? alreadyPresent
          ? "You already have repositories represented here"
          : "Ask the operator before adding a repository"
        : "Access policy needs human confirmation";
  const icon =
    checkState === "allowed" ? (
      <CheckCircle2 className="h-7 w-7" />
    ) : access.mode === "private" ? (
      <KeyRound className="h-7 w-7" />
    ) : (
      <ShieldQuestion className="h-7 w-7" />
    );
  const tone =
    checkState === "allowed"
      ? "border-emerald-500/25 bg-emerald-500/[0.04] text-emerald-700 dark:text-emerald-300"
      : checkState === "denied"
        ? "border-red-500/25 bg-red-500/[0.04] text-red-700 dark:text-red-300"
        : "border-violet-500/25 bg-violet-500/[0.04] text-violet-700 dark:text-violet-300";

  return (
    <section aria-labelledby="access-heading">
      <Card className={cn("overflow-hidden shadow-sm", tone)}>
        <CardContent className="p-0">
          <div className="grid lg:grid-cols-[minmax(0,1.3fr)_minmax(20rem,0.7fr)]">
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-current/10 p-3">{icon}</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] opacity-80">
                    Can I use it?
                  </p>
                  <h2
                    id="access-heading"
                    className="mt-1 text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
                  >
                    {heading}
                  </h2>
                  <p className="mt-3 max-w-2xl text-base text-muted-foreground">
                    {access.description}
                  </p>

                  {access.mode === "private" && (
                    <div className="mt-5">
                      {!signedIn ? (
                        <Button onClick={onSignIn}>
                          Sign in to check access
                        </Button>
                      ) : checkState === "idle" ||
                        checkState === "unavailable" ? (
                        <div>
                          <Button onClick={onCheckAccess}>
                            <KeyRound className="mr-2 h-4 w-4" />
                            {checkState === "unavailable"
                              ? "Try access check again"
                              : "Check my whitelist access"}
                          </Button>
                          <p className="mt-2 text-xs text-muted-foreground">
                            This asks your signer for a NIP-42 proof and reveals
                            your pubkey to this service only after you click.
                          </p>
                        </div>
                      ) : checkState === "checking" ? (
                        <Button disabled>
                          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
                          Checking access…
                        </Button>
                      ) : (
                        <p className="flex items-center gap-2 font-medium text-foreground">
                          {checkState === "allowed" ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                          {checkState === "allowed"
                            ? "Authenticated successfully as a service member."
                            : "The service rejected this account as restricted."}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-current/10 bg-background/55 p-6 text-foreground lg:border-l lg:border-t-0">
              <h3 className="font-semibold">What that means</h3>
              <div className="mt-4 space-y-4">
                <DecisionFact
                  icon={<Globe2 className="h-4 w-4" />}
                  label="Reading"
                  value={
                    access.mode === "private"
                      ? "Nostr authentication required"
                      : "Publicly readable"
                  }
                />
                <DecisionFact
                  icon={<GitBranch className="h-4 w-4" />}
                  label="Adding a repository"
                  value={
                    access.mode === "private"
                      ? "Whitelist members only"
                      : access.mode === "curated"
                        ? "Operator approval required"
                        : "Not clearly advertised"
                  }
                />
                {access.criteria && (
                  <DecisionFact
                    icon={<ShieldCheck className="h-4 w-4" />}
                    label="Operator policy"
                    value={access.criteria}
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function AccessSkeleton() {
  return (
    <Card className="shadow-none">
      <CardContent className="flex items-center gap-3 px-4 py-3">
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 w-64 max-w-[80%]" />
      </CardContent>
    </Card>
  );
}

function DecisionFact({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="mt-0.5 break-words text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

function PersonalSection({
  signedIn,
  personal,
  follows,
  showServiceActivity,
  serviceActivityUnavailable,
  showCoordinator,
  myActiveRepositories,
  myReadyRepositories,
  repositoriesLoading,
  onSignIn,
}: {
  signedIn: boolean;
  personal: PersonalGraspCounts | undefined;
  follows: FollowRepositoryPresence | undefined;
  showServiceActivity: boolean;
  serviceActivityUnavailable: boolean;
  showCoordinator: boolean;
  myActiveRepositories: ResolvedRepo[];
  myReadyRepositories: ResolvedRepo[];
  repositoriesLoading: boolean;
  onSignIn: () => void;
}) {
  return (
    <section aria-labelledby="for-you-heading">
      <div className="mb-4 flex items-center gap-3">
        <div className="rounded-lg bg-blue-500/10 p-2 text-blue-600 dark:text-blue-400">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h2
            id="for-you-heading"
            className="text-2xl font-bold tracking-tight"
          >
            For you
          </h2>
          <p className="text-sm text-muted-foreground">
            {showServiceActivity && showCoordinator
              ? "Your footprint, your network, and coordinator activity on your repositories."
              : showServiceActivity
                ? "Your footprint and the people you know on this service."
                : "How this coordinator relates to your repositories."}
          </p>
        </div>
      </div>

      {!signedIn ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-start justify-between gap-5 p-6 sm:flex-row sm:items-center sm:p-8">
            <div>
              <h3 className="text-xl font-semibold">
                {showServiceActivity
                  ? "See what this service means to you"
                  : "See how this coordinator relates to you"}
              </h3>
              <p className="mt-1 max-w-2xl text-muted-foreground">
                {showServiceActivity && showCoordinator
                  ? "Sign in to count your repositories and collaboration activity, find repositories from people you follow, and see whether this coordinator is acting on—or ready for—your projects."
                  : showServiceActivity
                    ? "Sign in to count your repositories and collaboration activity here, and find repositories from people you follow."
                    : "Sign in to see whether this coordinator is acting on—or ready to serve—your repositories."}
              </p>
            </div>
            <Button onClick={onSignIn} className="shrink-0">
              Sign in
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {showServiceActivity && (
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="h-5 w-5 text-pink-500" />
                  Your activity here
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <PersonalMetric
                    label="Repositories"
                    value={personal?.repositories}
                  />
                  <PersonalMetric label="Issues" value={personal?.issues} />
                  <PersonalMetric
                    label="Pull requests"
                    value={personal?.pullRequests}
                  />
                  <PersonalMetric
                    label="Interactions"
                    value={personal?.interactions}
                  />
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  Counts events authored by your active account on this service.
                  Interactions include comments, reactions, and status activity.
                </p>
              </CardContent>
            </Card>
          )}

          {showServiceActivity && (
            <FollowPresenceCard
              follows={follows}
              unavailable={serviceActivityUnavailable}
            />
          )}

          {showCoordinator && (
            <Card
              className={cn(
                "shadow-sm",
                showServiceActivity && "lg:col-span-2",
              )}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Cpu className="h-5 w-5 text-violet-500" />
                  Coordinator and your repositories
                </CardTitle>
              </CardHeader>
              <CardContent>
                {repositoriesLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-72" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ) : (
                  <div className="grid gap-5 md:grid-cols-2">
                    <PersonalRepoRelationship
                      title="Acting now"
                      description="The coordinator currently claims service for these repositories."
                      repositories={myActiveRepositories}
                      empty="It is not currently acting on any of your repositories."
                      tone="emerald"
                    />
                    <PersonalRepoRelationship
                      title="Ready if requested"
                      description="A maintainer start event can request service for these repositories."
                      repositories={myReadyRepositories}
                      empty="It has not advertised readiness for any additional repositories of yours."
                      tone="violet"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

function PersonalMetric({
  label,
  value,
}: {
  label: string;
  value: CountValue;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/25 p-3">
      {value === undefined ? (
        <Skeleton className="mb-2 h-7 w-10" />
      ) : value === null ? (
        <p
          className="text-2xl font-bold text-muted-foreground"
          title="Count unavailable"
        >
          —
        </p>
      ) : (
        <p className="text-2xl font-bold tabular-nums">{formatCount(value)}</p>
      )}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function FollowPresenceCard({
  follows,
  unavailable,
}: {
  follows: FollowRepositoryPresence | undefined;
  unavailable: boolean;
}) {
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5 text-blue-500" />
          People you follow
        </CardTitle>
      </CardHeader>
      <CardContent>
        {unavailable ? (
          <p className="text-muted-foreground">
            Followed-user presence is not queried without a retained private
            relay session, so private repository events do not enter the shared
            local cache.
          </p>
        ) : !follows ? (
          <div className="space-y-3">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : follows.people > 0 ? (
          <>
            <p className="text-lg font-semibold">
              {follows.people.toLocaleString()}{" "}
              {follows.people === 1 ? "person has" : "people have"}{" "}
              {follows.repositoryAnnouncements.toLocaleString()} repository
              {follows.repositoryAnnouncements === 1
                ? ""
                : " announcements"}{" "}
              here
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {follows.samplePubkeys.map((pubkey) => (
                <UserLink
                  key={pubkey}
                  pubkey={pubkey}
                  avatarSize="sm"
                  className="rounded-full border border-border/60 bg-muted/30 px-2.5 py-1.5"
                />
              ))}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground">
            No repository announcements from people you follow were found on
            this service.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PersonalRepoRelationship({
  title,
  description,
  repositories,
  empty,
  tone,
}: {
  title: string;
  description: string;
  repositories: ResolvedRepo[];
  empty: string;
  tone: "emerald" | "violet";
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "h-2.5 w-2.5 rounded-full",
            tone === "emerald" ? "bg-emerald-500" : "bg-violet-500",
          )}
        />
        <h3 className="font-semibold">
          {title} · {repositories.length}
        </h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      {repositories.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {repositories.slice(0, 6).map((repository) => (
            <RepoBadge
              key={repository.selectedCoordinate}
              coord={repository.selectedCoordinate}
              repoName={repository.name}
            />
          ))}
          {repositories.length > 6 && (
            <Badge variant="secondary">+{repositories.length - 6} more</Badge>
          )}
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

function CoordinatorSection({
  state,
  advertisementIsLive,
  activeCoordinates,
  targetCoordinates,
  recentRuns,
}: {
  state: CICoordinatorProfileState | undefined;
  advertisementIsLive: boolean;
  activeCoordinates: string[];
  targetCoordinates: string[];
  recentRuns: CIWorkflowRun[] | undefined;
}) {
  const advertisement = state?.advertisement;
  return (
    <section aria-labelledby="coordinator-heading">
      <div className="mb-4 flex items-center gap-3">
        <div className="rounded-lg bg-violet-500/10 p-2 text-violet-600 dark:text-violet-400">
          <Workflow className="h-5 w-5" />
        </div>
        <div>
          <h2
            id="coordinator-heading"
            className="text-2xl font-bold tracking-tight"
          >
            CI coordinator
          </h2>
          <p className="text-sm text-muted-foreground">
            What it can do, where it is active, and its latest workflow runs.
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <div className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-lg">Service summary</CardTitle>
                <Badge
                  variant="outline"
                  className={cn(
                    advertisementIsLive
                      ? "border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                      : "border-amber-500/30 text-amber-700 dark:text-amber-300",
                  )}
                >
                  {advertisementIsLive
                    ? "Available now"
                    : "Not advertising now"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {!state ? (
                <CoordinatorSummarySkeleton />
              ) : advertisement ? (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <CoordinatorMetric
                      label="Acting on"
                      value={activeCoordinates.length}
                    />
                    <CoordinatorMetric
                      label="Ready for"
                      value={targetCoordinates.length}
                    />
                    <CoordinatorMetric
                      label="Recent runs"
                      value={recentRuns?.length}
                    />
                  </div>
                  <CapabilitySummary advertisement={advertisement} />
                </>
              ) : (
                <div className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
                  No valid live capability advertisement was found. Historical
                  jobs may still appear, but availability cannot be assumed.
                </div>
              )}
            </CardContent>
          </Card>

          {(activeCoordinates.length > 0 || targetCoordinates.length > 0) && (
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Repository coverage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <CoordinateList
                  title="Acting now"
                  coordinates={activeCoordinates}
                  icon={<RadioTower className="h-4 w-4 text-emerald-500" />}
                  empty="No active repository claims."
                />
                <CoordinateList
                  title="Ready if requested"
                  coordinates={targetCoordinates}
                  icon={<GitBranch className="h-4 w-4 text-violet-500" />}
                  empty="No additional readiness targets."
                />
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock3 className="h-5 w-5 text-muted-foreground" />
              Recent jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentRuns === undefined ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : recentRuns.length > 0 ? (
              <div className="divide-y divide-border/60">
                {recentRuns.map((run) => (
                  <RecentRunRow key={run.key} run={run} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed px-6 py-10 text-center">
                <Workflow className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">
                  No recent workflow runs were found on the relevant Git relays.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function CoordinatorSummarySkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-24 rounded-lg" />
    </div>
  );
}

function CoordinatorMetric({
  label,
  value,
}: {
  label: string;
  value: number | undefined;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/25 p-3 text-center">
      {value === undefined ? (
        <Skeleton className="mx-auto mb-1 h-6 w-8" />
      ) : (
        <p className="text-xl font-bold tabular-nums">
          {value.toLocaleString()}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function capabilitySelectorLabel(selector: string): string {
  const [family, ...rest] = selector.split(":");
  const target = rest.join(":").replaceAll("-", " ");
  if (family === "act") return `GitHub Actions on ${target}`;
  if (family === "nix") return `Native Nix on ${target}`;
  return `${family.toUpperCase()} on ${target}`;
}

function CapabilitySummary({
  advertisement,
}: {
  advertisement: CICoordinatorAdvertisement;
}) {
  const admission =
    advertisement.admissionPolicy === "open"
      ? "Accepts requests from any repository"
      : advertisement.admissionPolicy === "maintainer-request"
        ? "Repository maintainers can request service"
        : "The operator chooses which repositories it serves";
  const execution =
    advertisement.executionPolicy === "automatic"
      ? "Runs workflows automatically"
      : "Starts runs when a maintainer requests them";
  const billing =
    advertisement.billingPolicy === "not-required"
      ? "No payment required"
      : advertisement.billingPolicy === "out-of-band"
        ? "Payment arranged with the operator"
        : "No billing policy advertised";

  return (
    <div className="mt-5 space-y-4">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Runners
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {advertisement.runnerSelectors.map((selector) => (
            <Badge key={selector} variant="secondary" className="font-normal">
              <Cpu className="mr-1.5 h-3.5 w-3.5" />
              {capabilitySelectorLabel(selector)}
            </Badge>
          ))}
        </div>
      </div>
      <div className="space-y-3 text-sm">
        <DecisionFact
          icon={<Users className="h-4 w-4" />}
          label="Who can request it"
          value={admission}
        />
        <DecisionFact
          icon={<Workflow className="h-4 w-4" />}
          label="When it runs"
          value={execution}
        />
        <DecisionFact
          icon={<KeyRound className="h-4 w-4" />}
          label="Repository secrets"
          value={
            advertisement.secretsRecipient
              ? "Accepts end-to-end encrypted secrets"
              : "No encrypted secret channel advertised"
          }
        />
        <DecisionFact
          icon={<Sparkles className="h-4 w-4" />}
          label="Cost"
          value={billing}
        />
      </div>
      {advertisement.software && (
        <p className="text-xs text-muted-foreground">
          Powered by {advertisement.software}
          {advertisement.version ? ` ${advertisement.version}` : ""}.
        </p>
      )}
    </div>
  );
}

function CoordinateList({
  title,
  coordinates,
  icon,
  empty,
}: {
  title: string;
  coordinates: string[];
  icon: ReactNode;
  empty: string;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title} · {coordinates.length}
      </div>
      {coordinates.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {coordinates.slice(0, 8).map((coordinate) => (
            <RepoBadge key={coordinate} coord={coordinate} />
          ))}
          {coordinates.length > 8 && (
            <Badge variant="secondary">+{coordinates.length - 8} more</Badge>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

function RecentRunRow({ run }: { run: CIWorkflowRun }) {
  const coordinate = workflowRunRepoCoords(run)[0] ?? "";
  const repoPath = useDefaultRepoCoordPath(coordinate);
  const workflowName = run.workflowPath?.split("/").pop() ?? "Workflow";
  const timeAgo = formatDistanceToNow(new Date(run.createdAt * 1000), {
    addSuffix: true,
  });
  return (
    <div className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
      <CIStatusIcon status={run.status} className="mt-1 h-5 w-5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="font-semibold">{workflowName}</p>
          <Badge variant="secondary" className="text-xs font-normal">
            {ciStatusLabel(run.status)}
          </Badge>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {coordinate && <RepoBadge coord={coordinate} to={repoPath} />}
          {run.trigger && <span>{run.trigger.replaceAll("_", " ")}</span>}
          <span>{timeAgo}</span>
        </div>
      </div>
    </div>
  );
}
